import { useCallback, useEffect } from "react";
import { useLocalStorage } from "./use-local-storage";

interface DropdownState {
  selectedValue: string | null;
  searchQuery: string;
  scrollPosition: number;
  isExpanded: boolean;
  recentSelections: string[];
  timestamp?: number;
}

export function useDropdownState(key: string, maxRecentItems: number = 10) {
  const storageKey = `dropdown_state_${key}`;
  const [state, setState] = useLocalStorage<DropdownState>(storageKey, {
    selectedValue: null,
    searchQuery: "",
    scrollPosition: 0,
    isExpanded: false,
    recentSelections: [],
  });

  // Clean up old entries after 7 days
  useEffect(() => {
    const cleanupOldEntries = () => {
      const cutoffTime = Date.now() - 7 * 24 * 60 * 60 * 1000; // 7 days
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith("dropdown_state_")) {
          try {
            const item = localStorage.getItem(key);
            if (item) {
              const parsed = JSON.parse(item);
              if (parsed.timestamp && parsed.timestamp < cutoffTime) {
                localStorage.removeItem(key);
              }
            }
          } catch {
            // Ignore parsing errors
          }
        }
      });
    };
    cleanupOldEntries();
  }, []);

  const updateSelectedValue = useCallback(
    (value: string | null) => {
      setState((prevState: DropdownState) => ({
        ...prevState,
        selectedValue: value,
        recentSelections: value
          ? [
              value,
              ...prevState.recentSelections.filter((v: string) => v !== value),
            ].slice(0, maxRecentItems)
          : prevState.recentSelections,
        timestamp: Date.now(),
      }));
    },
    [setState, maxRecentItems],
  );

  const updateSearchQuery = useCallback(
    (query: string) => {
      setState((prevState: DropdownState) => ({
        ...prevState,
        searchQuery: query,
      }));
    },
    [setState],
  );

  const updateScrollPosition = useCallback(
    (position: number) => {
      setState((prevState: DropdownState) => ({
        ...prevState,
        scrollPosition: position,
      }));
    },
    [setState],
  );

  const updateExpanded = useCallback(
    (expanded: boolean) => {
      setState((prevState: DropdownState) => ({
        ...prevState,
        isExpanded: expanded,
      }));
    },
    [setState],
  );

  const clearState = useCallback(() => {
    setState({
      selectedValue: null,
      searchQuery: "",
      scrollPosition: 0,
      isExpanded: false,
      recentSelections: [],
    });
  }, [setState]);

  return {
    state,
    updateSelectedValue,
    updateSearchQuery,
    updateScrollPosition,
    updateExpanded,
    clearState,
  };
}
